import { tokenService, decodeJwtPayload } from '@aimsos/auth';
import { apiWS as api, apiAuth } from './apiClient.js';

/**
 * Workspaces API for the Core Shell.
 *
 * Today, the full server contract for these endpoints is **not** yet
 * defined. The shapes below are a best-effort alignment with the existing
 * `apps/workspaces/src/services/workspace.js` endpoints and the Shell
 * switcher needs. They are tolerant of alternative field names (UUID vs
 * `id`, `name` vs `display_name`, etc.) so we can iterate without breaking
 * the front when the contract lands.
 *
 * Response normalization is inlined here to keep the Core Shell independent
 * of the `apps/workspaces/src/shared/utils/apiResponse` module (which lives
 * in a sibling app and is not importable across apps by convention).
 *
 * The canonical shape the rest of the shell sees:
 *
 *   {
 *     id: string,          // workspace UUID
 *     name: string,
 *     slug?: string,
 *     role?: string,       // e.g. "owner" | "admin" | "member"
 *     initials?: string,   // 2 chars, derived from name if missing
 *     avatarUrl?: string,  // tenant logo (S3 URL or similar)
 *     email?: string,
 *     isCurrent?: boolean,
 *   }
 */

/**
 * @typedef {Object} Workspace
 * @property {string} id
 * @property {string} name
 * @property {string} [slug]
 * @property {string} [role]
 * @property {string} [initials]
 * @property {string} [avatarUrl]
 * @property {string} [email]
 * @property {boolean} [isCurrent]
 */

const STORAGE_TENANT_KEY = 'workspace_uuid';

/**
 * Derive a 2-char uppercase initial from a workspace name.
 * Falls back to 'WS' if no usable chars.
 *
 * @param {string} name
 * @returns {string}
 */
function deriveInitials(name) {
  if (typeof name !== 'string' || !name.trim()) return 'WS';
  const cleaned = name.replace(/[^A-Za-z0-9 ]/g, '').trim();
  if (!cleaned) return 'WS';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'WS';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Normalize a raw API workspace object into the canonical shape.
 * Tolerant of: `uuid` / `id` / `workspace_uuid`, `name` / `display_name`,
 * `logo_url` / `avatar_url`, `user_role` / `role`, `contact_email` / `email`.
 *
 * @param {Record<string, any>} raw
 * @returns {Workspace}
 */
export function mapWorkspaceListItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  // The real server response uses `workspaceUuid` (and the legacy fields
  // `uuid` / `id` / `workspace_uuid` are still accepted for compatibility).
  const id = raw.workspaceUuid ?? raw.workspace_uuid ?? raw.uuid ?? raw.id;
  const name = raw.name ?? raw.display_name ?? raw.workspace_name ?? '';
  return {
    id: id != null ? String(id) : '',
    name: String(name),
    slug: raw.slug ?? undefined,
    role: raw.role ?? raw.user_role ?? raw.membership_role,
    initials: raw.initials ?? deriveInitials(name),
    avatarUrl: raw.profilePhotoUrl ?? raw.logo_url ?? raw.avatar_url ?? raw.photo_url,
    email: raw.contact_email ?? raw.email,
    status: raw.status,
    isCurrent: raw.is_current ?? raw.isCurrent,
  };
}

/**
 * Same shape as list item — detail responses can be richer but the shell
 * only consumes the canonical fields.
 *
 * @param {Record<string, any>} raw
 * @returns {Workspace}
 */
export function mapWorkspaceDetail(raw) {
  return mapWorkspaceListItem(raw);
}

/**
 * Extract the body of a response that may be wrapped in `{ body, meta }`,
 * `{ items, total }`, or be a raw array.
 *
 * @param {unknown} response
 * @returns {any}
 */
function unwrapBody(response) {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.body)) return data.body;
    if (Array.isArray(data.items)) return data.items;
    if (data.body && typeof data.body === 'object') return data.body;
  }
  return null;
}

/**
 * Throw a normalized error with status info if the body is a structured
 * error response (status_code >= 400 in the payload).
 *
 * @param {unknown} response
 * @param {string} fallbackMessage
 */
function throwIfErrorResponse(response, fallbackMessage) {
  const data = response?.data ?? response;
  if (!data || typeof data !== 'object') return;
  const statusCode = data.status_code ?? data.statusCode;
  if (typeof statusCode === 'number' && statusCode >= 400) {
    const msgCode = data.msg_code ?? data.msgCode ?? [];
    const message =
      (Array.isArray(msgCode) && msgCode[0] && (msgCode[0].description ?? msgCode[0].code)) ||
      data.message ||
      fallbackMessage;
    const err = new Error(message);
    err.response = { data, status: statusCode };
    throw err;
  }
}

/**
 * List workspaces the current user has access to.
 *
 * Calls `POST /v0/workspace/list` (the listing endpoint used by the
 * workspaces app). The route lives at the ROOT of the workspaces gateway
 * (`VITE_API_BASE_URL_WS`) — there is no `/workspace` aggregate prefix in
 * front of it; adding one returns a 404 that, having no CORS headers on an
 * unmatched route, surfaces in the browser as an opaque "CORS error".
 * The real server response wraps the list under
 * `body` as a plain array of:
 *
 *   {
 *     workspaceUuid: string,
 *     name: string,
 *     status: 'in_progress' | 'active' | ...,
 *     profilePhotoUrl?: string,
 *     createdAt: string,
 *     userCount: number,
 *     ownerName: string,
 *   }
 *
 * @param {{ page?: number, page_size?: number, search?: string }} [params]
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ items: Workspace[], total?: number }>}
 */
export async function listMyWorkspaces(params = {}, signal) {
  const body = {
    page: params.page ?? 1,
    page_size: params.page_size ?? 15,
    order_by: ['created_at'],
    order_direction: ['asc'],
  };
  if (params.search) {
    body.search = { keyword: params.search };
  }
  const response = await api.post('/v0/workspace/list', body, signal ? { signal } : undefined);
  throwIfErrorResponse(response, 'Failed to load workspaces');

  const list = unwrapBody(response) ?? [];
  if (!Array.isArray(list)) {
    // Defensive: log the actual response shape once so we can fix the
    // mapper quickly if the server changes its contract.
    if (import.meta.env.DEV) {
      console.warn('[workspaces] list response is not an array:', list);
    }
  }
  const items = list.map(mapWorkspaceListItem).filter(Boolean);
  const data = response?.data ?? response;
  const meta = data?.meta ?? null;
  const total = meta?.total ?? data?.total ?? items.length;
  return { items, meta, total };
}

/**
 * Fetch a single workspace by UUID.
 *
 * Calls `GET /v0/workspace/<workspaceId>`. The response body uses different
 * field names than the list endpoint (`workspaceName` instead of `name`,
 * `photoUrl` instead of `profilePhotoUrl`, etc.) so we normalise it into
 * the same canonical Workspace shape used everywhere in the shell.
 *
 * @param {string} workspaceId
 * @param {AbortSignal} [signal]
 * @returns {Promise<Workspace>}
 */
export async function getWorkspaceById(workspaceId, signal) {
  if (!workspaceId) throw new Error('getWorkspaceById requires a workspaceId');

  const response = await api.get(
    `/v0/workspace/${workspaceId}`,
    signal ? { signal } : undefined,
  );
  throwIfErrorResponse(response, 'Failed to load workspace');

  const raw = unwrapBody(response) ?? response?.data ?? response ?? {};
  // Detail endpoint uses workspaceName / photoUrl instead of name / profilePhotoUrl.
  const name =
    raw.workspaceName ?? raw.workspace_name ?? raw.name ?? raw.display_name ?? '';
  const id =
    raw.workspaceUuid ?? raw.workspace_uuid ?? raw.uuid ?? raw.id ?? workspaceId;
  return {
    id: String(id),
    name: String(name),
    slug: raw.slug ?? undefined,
    role: raw.role ?? raw.user_role ?? undefined,
    initials: raw.initials ?? deriveInitials(name),
    avatarUrl: raw.photoUrl ?? raw.photo_url ?? raw.profilePhotoUrl ?? raw.logo_url ?? undefined,
    email: raw.ownerEmail ?? raw.owner_email ?? raw.contact_email ?? raw.email ?? undefined,
    status: raw.status ?? undefined,
  };
}

/**
 * Switch the active workspace.
 *
 * Calls `POST /v1/workspace/switch` with the target workspace UUID. The
 * endpoint is documented in the platform OpenAPI spec (authorization.yaml):
 *
 *   Request:
 *     POST /v1/workspace/switch
 *     Body: { workspace_uuid: string }
 *
 *   Response (200):
 *     {
 *       statusCode: 200,
 *       msgCode: [],
 *       requestId: string,
 *       body: {
 *         sessionId: string,
 *         workspaceUuid: string,
 *         accessToken: string,
 *         refreshToken: string,
 *       }
 *     }
 *
 *   Error responses: 400, 401, 404, 409, 500.
 *
 * Returns the parsed body so the caller can update the auth client and
 * localStorage with the new tokens.
 *
 * @param {string} workspaceId
 * @returns {Promise<{
 *   sessionId: string,
 *   workspaceUuid: string,
 *   accessToken: string,
 *   refreshToken: string,
 * }>}
 */
/**
 * Exchange a new backend JWT scoped to `workspaceId`.
 *
 * Calls `POST /v0/token` with the payload:
 *   { workspace_uuid: string, user_uuid: string }
 *
 * On success the returned token is applied via `tokenService.setToken()` so
 * the Axios client immediately uses it for subsequent requests, and the
 * proactive-refresh timer in `Auth0AuthBridge` is rescheduled.
 *
 * @param {string} workspaceId  Target workspace UUID.
 * @param {string} [userUuid]   Current user UUID. When absent or empty the
 *                              function falls back to the `user_uuid` claim
 *                              inside the current backend JWT so that calls
 *                              made before Auth0AuthBridge writes the dedicated
 *                              localStorage key still succeed.
 * @returns {Promise<string>}   The new JWT.
 */
export async function fetchTokenForWorkspace(workspaceId, userUuid) {
  if (!workspaceId) {
    throw new Error('fetchTokenForWorkspace requires workspaceId');
  }

  // `user_uuid` may not yet be in localStorage when the user switches
  // workspaces before Auth0AuthBridge finishes its initial token exchange.
  // The backend JWT is written to `api_access_token` first, so decode it
  // as a fallback — the payload always contains `user_uuid`.
  let resolvedUserUuid = userUuid;
  if (!resolvedUserUuid) {
    const currentToken = tokenService.getToken();
    if (currentToken) {
      resolvedUserUuid = decodeJwtPayload(currentToken)?.user_uuid || '';
    }
  }

  if (!resolvedUserUuid) {
    throw new Error('fetchTokenForWorkspace: user_uuid unavailable — auth not yet initialised');
  }

  const response = await apiAuth.post('/v0/token', {
    workspace_uuid: workspaceId,
    user_uuid: resolvedUserUuid,
  });
  throwIfErrorResponse(response, 'Failed to get token for workspace');

  // api.post returns response.data (interceptor unwraps the axios envelope).
  // The token endpoint wraps the JWT under body.token.
  const token = response?.body?.token ?? response?.token;
  if (!token) {
    throw new Error('Token response missing token field');
  }
  tokenService.setToken(token);
  return token;
}

/**
 * Persist the active workspace id locally so the rest of the shell picks it
 * up before the next request. Centralized here to keep the storage key in
 * one place.
 *
 * @param {string} workspaceId
 */
export function persistActiveWorkspaceId(workspaceId) {
  if (!workspaceId) return;
  try {
    localStorage.setItem(STORAGE_TENANT_KEY, String(workspaceId));
  } catch (e) {
    // localStorage can throw in private mode — fail silently, the back is
    // still the source of truth.
    if (import.meta.env.DEV) {
      console.warn('[workspaces] localStorage.setItem failed:', e);
    }
  }
}

/**
 * Read the locally persisted active workspace id.
 *
 * @returns {string}
 */
export function readPersistedWorkspaceId() {
  try {
    return localStorage.getItem(STORAGE_TENANT_KEY) || '';
  } catch {
    return '';
  }
}
